// components/alerts/AlertSettings.tsx
// Alert configuration form for managing thresholds, channels, and recipients

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { sendTestAlert, AlertChannel } from '../../services/omoreAlertingService';
import type { AlertConfig } from '../../services/omoreAlertingService';
import type { FlagCategory } from '../../types/OmoreInteractionTypes';
import './AlertSettings.css';

const ALL_CATEGORIES: { value: FlagCategory; label: string }[] = [
    { value: 'self_harm', label: 'Self-Harm (Severity 5)' },
    { value: 'violence', label: 'Violence (Severity 4)' },
    { value: 'sexual', label: 'Sexual (Severity 4)' },
    { value: 'harassment', label: 'Harassment (Severity 3)' },
    { value: 'hate', label: 'Hate Speech (Severity 3)' },
    { value: 'inappropriate', label: 'Inappropriate (Severity 2)' },
    { value: 'prompt_injection', label: 'Prompt Injection (Severity 2)' },
    { value: 'other', label: 'Other (Severity 1)' },
];

const ALL_CHANNELS: { value: AlertChannel; label: string; description: string }[] = [
    { value: AlertChannel.IN_APP, label: 'In-App', description: 'Browser notifications and alert panel' },
    { value: AlertChannel.EMAIL, label: 'Email', description: 'Send email alerts (requires SendGrid/SES setup)' },
    { value: AlertChannel.SLACK, label: 'Slack', description: 'Post to Slack channel (requires webhook setup)' },
    { value: AlertChannel.SMS, label: 'SMS', description: 'Send SMS for CRITICAL only (requires Twilio/SNS setup)' },
];

export const AlertSettings: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();

    // Config state
    const [criticalCategories, setCriticalCategories] = useState<FlagCategory[]>([
        'self_harm', 'violence', 'sexual',
    ]);
    const [minSeverityThreshold, setMinSeverityThreshold] = useState(4);
    const [channels, setChannels] = useState<AlertChannel[]>([
        AlertChannel.IN_APP, AlertChannel.EMAIL,
    ]);
    const [recipients, setRecipients] = useState<string[]>(
        currentUser?.email ? [currentUser.email] : []
    );
    const [newRecipient, setNewRecipient] = useState('');
    const [maxAlertsPerHour, setMaxAlertsPerHour] = useState(10);
    const [quietHoursStart, setQuietHoursStart] = useState<number | undefined>(undefined);
    const [quietHoursEnd, setQuietHoursEnd] = useState<number | undefined>(undefined);
    const [quietHoursEnabled, setQuietHoursEnabled] = useState(false);

    const [testLoading, setTestLoading] = useState(false);
    const [testResult, setTestResult] = useState<string>('');
    const [saveMessage, setSaveMessage] = useState('');

    const toggleCategory = (cat: FlagCategory) => {
        setCriticalCategories(prev =>
            prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
        );
    };

    const toggleChannel = (channel: AlertChannel) => {
        setChannels(prev =>
            prev.includes(channel) ? prev.filter(c => c !== channel) : [...prev, channel]
        );
    };

    const addRecipient = () => {
        const email = newRecipient.trim();
        if (email && !recipients.includes(email) && email.includes('@')) {
            setRecipients(prev => [...prev, email]);
            setNewRecipient('');
        }
    };

    const removeRecipient = (email: string) => {
        setRecipients(prev => prev.filter(r => r !== email));
    };

    const buildConfig = (): AlertConfig => ({
        criticalCategories,
        minSeverityThreshold,
        channels,
        recipients,
        maxAlertsPerHour,
        quietHoursStart: quietHoursEnabled ? (quietHoursStart || 22) : undefined,
        quietHoursEnd: quietHoursEnabled ? (quietHoursEnd || 6) : undefined,
    });

    const handleSave = () => {
        // In a full implementation, this would persist to Firestore
        // For now, the config is used by the AlertContext subscription
        const config = buildConfig();
        console.log('Alert config saved:', config);
        setSaveMessage('Configuration saved successfully.');
        setTimeout(() => setSaveMessage(''), 3000);
    };

    const handleTestAlert = async () => {
        try {
            setTestLoading(true);
            setTestResult('');
            const config = buildConfig();
            const alert = await sendTestAlert(config);
            setTestResult(`Test alert created with ID: ${alert.id}. Check the alert panel.`);
        } catch (err: any) {
            console.error('Error sending test alert:', err);
            setTestResult(`Failed to send test alert: ${err.message}`);
        } finally {
            setTestLoading(false);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    return (
        <div className="alert-settings-container">
            <header className="alert-settings-header">
                <div className="header-content">
                    <div>
                        <h1>Alert Settings</h1>
                        <p className="header-subtitle">Configure moderation alert thresholds and notification channels</p>
                    </div>
                    <div className="header-actions">
                        <button onClick={() => navigate('/ai')} className="back-button">← AI Dashboard</button>
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">Sign Out</button>
                    </div>
                </div>
            </header>

            <div className="alert-settings-main">
                {/* Category Selection */}
                <section className="settings-section">
                    <h2>Alert Categories</h2>
                    <p className="section-desc">Select which flag categories should trigger alerts.</p>
                    <div className="category-checkboxes">
                        {ALL_CATEGORIES.map(cat => (
                            <label key={cat.value} className="checkbox-item">
                                <input
                                    type="checkbox"
                                    checked={criticalCategories.includes(cat.value)}
                                    onChange={() => toggleCategory(cat.value)}
                                />
                                <span>{cat.label}</span>
                            </label>
                        ))}
                    </div>
                </section>

                {/* Severity Threshold */}
                <section className="settings-section">
                    <h2>Minimum Severity Threshold</h2>
                    <p className="section-desc">Only trigger alerts for flags at or above this severity level.</p>
                    <div className="severity-slider">
                        <input
                            type="range"
                            min={1}
                            max={5}
                            value={minSeverityThreshold}
                            onChange={e => setMinSeverityThreshold(parseInt(e.target.value))}
                        />
                        <div className="severity-labels">
                            <span>1 (Low)</span>
                            <span>2</span>
                            <span>3 (Medium)</span>
                            <span>4 (High)</span>
                            <span>5 (Critical)</span>
                        </div>
                        <div className="severity-current">Current: {minSeverityThreshold}</div>
                    </div>
                </section>

                {/* Notification Channels */}
                <section className="settings-section">
                    <h2>Notification Channels</h2>
                    <p className="section-desc">Choose how you want to receive alerts.</p>
                    <div className="channel-checkboxes">
                        {ALL_CHANNELS.map(ch => (
                            <label key={ch.value} className="checkbox-item channel-item">
                                <input
                                    type="checkbox"
                                    checked={channels.includes(ch.value)}
                                    onChange={() => toggleChannel(ch.value)}
                                />
                                <div>
                                    <span className="channel-name">{ch.label}</span>
                                    <span className="channel-desc">{ch.description}</span>
                                </div>
                            </label>
                        ))}
                    </div>
                </section>

                {/* Recipients */}
                <section className="settings-section">
                    <h2>Email Recipients</h2>
                    <p className="section-desc">Who should receive email alerts?</p>
                    <div className="recipients-list">
                        {recipients.map(email => (
                            <div key={email} className="recipient-chip">
                                <span>{email}</span>
                                <button onClick={() => removeRecipient(email)} className="remove-chip">×</button>
                            </div>
                        ))}
                    </div>
                    <div className="add-recipient">
                        <input
                            type="email"
                            value={newRecipient}
                            onChange={e => setNewRecipient(e.target.value)}
                            placeholder="Add email address..."
                            onKeyDown={e => e.key === 'Enter' && addRecipient()}
                        />
                        <button onClick={addRecipient} className="add-btn">Add</button>
                    </div>
                </section>

                {/* Rate Limiting */}
                <section className="settings-section">
                    <h2>Rate Limiting</h2>
                    <div className="rate-limit-group">
                        <label>Max alerts per hour:</label>
                        <input
                            type="number"
                            min={1}
                            max={100}
                            value={maxAlertsPerHour}
                            onChange={e => setMaxAlertsPerHour(parseInt(e.target.value) || 10)}
                        />
                    </div>
                </section>

                {/* Quiet Hours */}
                <section className="settings-section">
                    <h2>Quiet Hours (UTC)</h2>
                    <label className="checkbox-item">
                        <input
                            type="checkbox"
                            checked={quietHoursEnabled}
                            onChange={e => setQuietHoursEnabled(e.target.checked)}
                        />
                        <span>Enable quiet hours (suppress non-critical alerts)</span>
                    </label>
                    {quietHoursEnabled && (
                        <div className="quiet-hours-inputs">
                            <div className="time-input">
                                <label>Start (UTC hour):</label>
                                <input
                                    type="number"
                                    min={0}
                                    max={23}
                                    value={quietHoursStart || 22}
                                    onChange={e => setQuietHoursStart(parseInt(e.target.value))}
                                />
                            </div>
                            <div className="time-input">
                                <label>End (UTC hour):</label>
                                <input
                                    type="number"
                                    min={0}
                                    max={23}
                                    value={quietHoursEnd || 6}
                                    onChange={e => setQuietHoursEnd(parseInt(e.target.value))}
                                />
                            </div>
                        </div>
                    )}
                </section>

                {/* Actions */}
                <section className="settings-actions">
                    <button onClick={handleSave} className="save-button">
                        Save Configuration
                    </button>
                    <button
                        onClick={handleTestAlert}
                        className="test-button"
                        disabled={testLoading}
                    >
                        {testLoading ? 'Sending...' : 'Send Test Alert'}
                    </button>
                    {saveMessage && <div className="save-message">{saveMessage}</div>}
                    {testResult && <div className="test-result">{testResult}</div>}
                </section>
            </div>
        </div>
    );
};
